import React, { useEffect, useRef, useState } from "react";
import { useTurnstile } from "@/components/Turnstile";
import axios from "axios";
import { gaEvent } from "@/lib/track";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2 } from "lucide-react";
import ParticleHero from "@/components/ParticleHero";
import RankMedals from "@/components/home/RankMedals";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { setPlatformToken } from "@/lib/musicApi";
import { MagicCodeEntry } from "@/components/auth/MagicCodeEntry";
import { applySeo, canonicalFor } from "@/lib/seo";
import { useLaunch, useLaunchPercent } from "@/lib/useLaunch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pad = (n) => String(n).padStart(2, "0");

// Countdown colour stops (approved): white > 14d, amber 14-3d, orange 72-12h,
// red under 12h with a pulse in the final hour.
const countdownColor = (secs) => {
  if (secs > 14 * 86400) return "#FFFFFF";
  if (secs > 3 * 86400) return "#f59e0b";
  if (secs > 12 * 3600) return "#f97316";
  return "#ef4444";
};

const Countdown = ({ remaining }) => {
  const { t } = useTranslation();
  const secs = Math.max(0, Math.floor(remaining / 1000));
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const color = countdownColor(secs);
  const pulse = secs > 0 && secs <= 3600;
  const Unit = ({ v, label, tid }) => (
    <div className="flex flex-col items-center" data-testid={tid}>
      <span className="tabular-nums font-black leading-none" style={{ color, fontSize: "clamp(2.5rem, 9vw, 5.5rem)" }}>{pad(v)}</span>
      <span className="text-xs sm:text-sm mt-2 tracking-widest uppercase" style={{ color: "var(--text-secondary)" }}>{label}</span>
    </div>
  );
  return (
    <div
      data-testid="launch-countdown"
      className={`flex items-start justify-center gap-4 sm:gap-8 ${pulse ? "animate-pulse" : ""}`}
    >
      <Unit v={days} label={t("common.days", { defaultValue: "Days" })} tid="cd-days" />
      <Unit v={hours} label={t("common.hours", { defaultValue: "Hours" })} tid="cd-hours" />
      <Unit v={mins} label={t("common.minutes", { defaultValue: "Minutes" })} tid="cd-mins" />
      <Unit v={s} label={t("common.seconds", { defaultValue: "Seconds" })} tid="cd-secs" />
    </div>
  );
};

const UncheckDialog = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[10020] flex items-center justify-center p-4" style={{ background: "rgba(10,15,26,0.7)" }} data-testid="reminder-uncheck-dialog">
      <div className="w-full max-w-sm rounded-2xl p-6 text-center" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
        <h3 className="text-lg font-bold mb-2" style={{ color: "var(--text-primary)" }}>{t("launch.uncheckTitle")}</h3>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>{t("launch.uncheckBody")}</p>
        <div className="flex flex-col gap-2">
          <button onClick={onCancel} data-testid="reminder-uncheck-cancel" className="px-5 py-2.5 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff" }}>{t("launch.uncheckCancel")}</button>
          <button onClick={onConfirm} data-testid="reminder-uncheck-confirm" className="px-5 py-2.5 rounded-full text-sm font-medium" style={{ border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>{t("launch.uncheckConfirm")}</button>
        </div>
      </div>
    </div>
  );
};

const SignupCard = ({ testid }) => {
  const { t } = useTranslation();
  const percent = useLaunchPercent();
  const { refresh } = usePlatformAuth();
  const [email, setEmail] = useState("");
  const [reminder, setReminder] = useState(true);
  const [askUncheck, setAskUncheck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [pendingId, setPendingId] = useState("");
  const [signedIn, setSignedIn] = useState(false);

  const toggleReminder = () => {
    if (reminder) { setAskUncheck(true); } else { setReminder(true); }
  };

  const tsLaunch = useTurnstile("launch_signup");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email.trim()) { setErr(t("auth.emailPlaceholder", { defaultValue: "Enter your email" })); return; }
    setBusy(true);
    try {
      // withCredentials so the browser-binding nonce cookie round-trips.
      const { data } = await axios.post(`${API}/auth/subscriber/launch-signup`, { email: email.trim(), reminder_opt_in: reminder, turnstile_token: await tsLaunch() }, { withCredentials: true });
      setPendingId(data?.pending_id || "");
      gaEvent("generate_lead", { lead_type: "launch_signup" });
      setDone(true);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : t("auth.genericError", { defaultValue: "Something went wrong. Try again." }));
      setBusy(false);
    }
  };

  if (done) return (
    <div data-testid={`${testid}-done`} className="w-full max-w-md mx-auto rounded-2xl p-7 text-center" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
      <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "var(--accent-primary)" }} />
      {percent && <p className="text-sm" style={{ color: "var(--text-secondary)" }} data-testid="launch-confirm-success-body">{t("launch.confirmSuccessBody", { percent })}</p>}
      {pendingId && !signedIn && (
        <MagicCodeEntry pendingId={pendingId} onSuccess={async (data) => { if (data.token) setPlatformToken(data.token); await refresh(); setSignedIn(true); }} />
      )}
    </div>
  );

  return (
    <>
      <form onSubmit={submit} data-testid={testid} className="w-full max-w-md mx-auto rounded-2xl p-6 sm:p-7" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }}>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          placeholder={t("auth.emailPlaceholder", { defaultValue: "Your email" })}
          data-testid={`${testid}-email`} autoComplete="email"
          className="w-full mb-4 px-4 py-3 rounded-lg text-sm outline-none"
          style={{ background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
        />
        <label className="flex items-start gap-3 mb-4 cursor-pointer select-none" data-testid={`${testid}-reminder-label`}>
          <input type="checkbox" checked={reminder} onChange={toggleReminder} data-testid={`${testid}-reminder`} className="mt-0.5 h-4 w-4" style={{ accentColor: "var(--accent-primary)" }} />
          <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("launch.reminderLabel")}</span>
        </label>
        {err && <p className="text-sm mb-3" style={{ color: "#ef4444" }} data-testid={`${testid}-error`}>{err}</p>}
        <button type="submit" disabled={busy} data-testid={`${testid}-submit`} className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent-primary)", color: "#fff", opacity: busy ? 0.6 : 1 }}>
          {busy && <Loader2 size={15} className="animate-spin" />}{t("launch.createAccount")}
        </button>
      </form>
      {askUncheck && (
        <UncheckDialog
          onConfirm={() => { setReminder(false); setAskUncheck(false); }}
          onCancel={() => { setReminder(true); setAskUncheck(false); }}
        />
      )}
    </>
  );
};

const Landing = () => {
  const { t } = useTranslation();
  const { platformUser } = usePlatformAuth();
  const [remaining, setRemaining] = useState(null);
  const offsetRef = useRef(0);
  const launchRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    applySeo({
      title: "SAMTWO - Watch TV Shows Online, Stream Music Online",
      description: t("launch.dateLine"),
      canonical: canonicalFor("/"),
    });
  }, [t]);

  const launch = useLaunch();
  const percent = useLaunchPercent();
  useEffect(() => {
    if (!launch || !launch.launch_at) return undefined;
    const serverNow = new Date(launch.server_now).getTime();
    launchRef.current = new Date(launch.launch_at).getTime();
    offsetRef.current = Date.now() - serverNow; // browser clock minus server clock
    const tick = () => setRemaining(launchRef.current - (Date.now() - offsetRef.current));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [launch]);

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <main data-testid="launch-landing" className="min-h-screen" style={{ background: "var(--bg-primary)", color: "var(--text-primary)" }}>
      <section ref={topRef} className="pt-24 pb-8 px-4 flex flex-col items-center">
        {remaining != null && <Countdown remaining={remaining} />}
        <p data-testid="launch-date-line" className="text-base md:text-lg mt-8 text-center" style={{ color: "var(--text-secondary)" }}>
          {t("launch.dateLine")}
        </p>
      </section>

      <ParticleHero minimal />

      <section className="px-4 pb-4 -mt-8">
        <p data-testid="launch-stream-all" className="text-h3 text-center max-w-md mx-auto mb-6" style={{ color: "var(--text-primary)", textWrap: "balance" }}>
          {t("launch.streamAll")}
        </p>
        {platformUser ? (
          <p data-testid="launch-logged-in" className="text-center text-base md:text-lg max-w-md mx-auto" style={{ color: "var(--text-primary)" }}>
            {t("launch.loggedIn")}
          </p>
        ) : (
          <SignupCard testid="launch-signup-top" />
        )}
      </section>

      <RankMedals stripped />

      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-base md:text-lg font-bold mb-6" data-testid="launch-night-heading" style={{ color: "var(--text-primary)" }}>{t("launch.nightHeading")}</h2>
          <div className="flex flex-col gap-3">
            {["nightLine1", "nightLine2", "nightLine3", "nightLine4"].filter((k) => k !== "nightLine2" || percent).map((k) => (
              <p key={k} data-testid={`launch-${k}`} className="text-sm md:text-base" style={{ color: "var(--text-secondary)" }}>{t(`launch.${k}`, { percent })}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 px-4">
        <div className="max-w-xl mx-auto text-center rounded-2xl px-6 py-10" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)" }} data-testid="launch-first-hundred">
          <h3 className="text-base md:text-lg font-bold mb-2" data-testid="launch-first-hundred-heading" style={{ color: "var(--text-primary)" }}>{t("launch.firstHundredHeading")}</h3>
          {percent && <p className="text-sm md:text-base" data-testid="launch-first-hundred-line" style={{ color: "var(--text-secondary)" }}>{t("launch.firstHundredLine", { percent })}</p>}
        </div>
      </section>

      {!platformUser && (
        <section className="pb-20 px-4 flex justify-center">
          <button onClick={scrollTop} data-testid="launch-signup-bottom" className="px-8 py-3.5 rounded-full text-sm font-semibold" style={{ background: "var(--accent-primary)", color: "#fff" }}>
            {t("launch.createAccount")}
          </button>
        </section>
      )}

      <footer className="py-10 px-4 border-t text-center" style={{ borderColor: "var(--border-color)" }} data-testid="launch-footer">
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-4 text-xs" style={{ color: "var(--text-secondary)" }}>
          <a href="/terms" className="hover:underline" data-testid="footer-terms">{t("footer.terms", { defaultValue: "Terms" })}</a>
          <a href="/privacy" className="hover:underline" data-testid="footer-privacy">{t("footer.privacy", { defaultValue: "Privacy" })}</a>
          <a href="/cookies" className="hover:underline" data-testid="footer-cookies">{t("footer.cookies", { defaultValue: "Cookies" })}</a>
          <a href="/refund" className="hover:underline" data-testid="footer-refund">{t("footer.refund", { defaultValue: "Refund" })}</a>
          <a href="/dmca" className="hover:underline" data-testid="footer-dmca">DMCA</a>
        </div>
        <p className="text-xs" style={{ color: "var(--text-secondary)" }} data-testid="footer-copyright">&copy; {new Date().getFullYear()} SAMTWO, LLC. {t("footer.rights")}</p>
      </footer>
    </main>
  );
};

export default Landing;
